import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useScroll, useTransform, AnimatePresence, type Variants } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ChevronDown, X, MapPin, Clock, Users, Zap } from 'lucide-react';

type Page = 'home' | 'booking' | 'events' | 'membership' | 'contact';

interface HomePageProps {
  onNavigate: (page: Page) => void;
}

const useInView = (threshold = 0.12) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
};

/*
  `ease` must be a FOUR-TUPLE for framer-motion, not a `number[]`. Without the
  assertion the array widens to `number[]`, which is not assignable to `Easing`,
  and every `variants={fadeUp}` below was a type error.
*/
const fadeUp: Variants = {
  hidden: { opacity: 0, y: 36 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.85, delay: i * 0.13, ease: [0.22, 1, 0.36, 1] as const }
  })
};


const HeroSection: React.FC<{ onNavigate: (p: Page) => void }> = ({ onNavigate }) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const [loaded, setLoaded] = useState(false);

  return (
    <section ref={heroRef} className="relative h-screen min-h-[680px] overflow-hidden bg-black">
      <motion.div style={{ y: imgY }} className="absolute inset-0 will-change-transform">
        <img
          src="/photos/padel-behind-playing.jpg"
          alt="Padel Court SV Heinersreuth"
          className="w-full h-full object-cover scale-[1.08]"
          onLoad={() => setLoaded(true)}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/85" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/10 to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: contentY }}
        className="relative z-10 h-full flex flex-col justify-end px-6 sm:px-10 max-w-7xl mx-auto w-full"
      >
        <div className="pb-20 sm:pb-32 max-w-3xl">
          <AnimatePresence>
            {loaded && (
              <motion.div initial="hidden" animate="visible">
                <motion.div custom={0} variants={fadeUp} className="flex items-center gap-3 mb-8">
                  <div className="w-8 h-[1.5px] bg-red-500" />
                  <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-red-400/90">
                    Padel & Tennis · Bayreuth
                  </span>
                </motion.div>

                <motion.h1
                  custom={1}
                  variants={fadeUp}
                  className="text-[clamp(3rem,7.5vw,6.5rem)] font-bold text-white leading-[0.95] tracking-[-0.02em] mb-7"
                >
                  SV Heinersreuth
                </motion.h1>

                <motion.p
                  custom={2}
                  variants={fadeUp}
                  className="text-[clamp(0.95rem,1.5vw,1.15rem)] text-white/55 mb-10 leading-[1.7] max-w-md font-light tracking-[0.01em]"
                >
                  Der einzige Padel-Court in Bayreuth. Online buchen — schnell, einfach, sofort bestätigt.
                </motion.p>

                <motion.div custom={3} variants={fadeUp} className="flex flex-wrap gap-3">
                  <button
                    onClick={() => onNavigate('booking')}
                    className="group inline-flex items-center gap-2.5 bg-red-600 hover:bg-red-500 text-white px-7 py-3.5 text-[13px] font-semibold rounded-2xl transition-all duration-300 hover:shadow-[0_12px_40px_rgba(220,38,38,0.45)] hover:-translate-y-0.5 tracking-wide"
                  >
                    Jetzt buchen
                    <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                  </button>
                  <button
                    onClick={() => onNavigate('membership')}
                    className="inline-flex items-center gap-2 bg-white/8 backdrop-blur-sm hover:bg-white/16 text-white border border-white/14 px-7 py-3.5 text-[13px] font-semibold rounded-2xl transition-all duration-300 tracking-wide"
                  >
                    Mitglied werden
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: loaded ? 1 : 0 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 sm:right-10 z-10 flex gap-6 sm:gap-8"
      >
        {[
          { value: '3', label: 'Tennisplätze' },
          { value: '2', label: 'Padel Courts' },
        ].map((s, i) => (
          <div key={i} className="text-right">
            <div className="text-[1.9rem] sm:text-[3rem] font-bold text-white leading-none tracking-tight">{s.value}</div>
            <div className="text-[9px] sm:text-[10px] text-white/35 mt-1.5 tracking-[0.14em] uppercase">{s.label}</div>
          </div>
        ))}
      </motion.div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: loaded ? 1 : 0 }}
        transition={{ delay: 2, duration: 1 }}
        onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}
        className="absolute bottom-9 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-white/25 hover:text-white/50 transition-colors duration-300"
      >
        <motion.div
          animate={{ y: [0, 5, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
        >
          <ChevronDown size={18} strokeWidth={1.5} />
        </motion.div>
      </motion.button>

      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-black/60 to-transparent pointer-events-none z-[1]" />
    </section>
  );
};

const MarqueeBar: React.FC = () => {
  const items = [
    { text: '3 Tennisplätze', accent: false },
    { text: 'SV Heinersreuth', accent: false },
    { text: '2 Padel Courts', accent: false },
    { text: 'Online Buchung', accent: false },
    { text: 'Flutlicht bis 23:00 Uhr', accent: false },
    { text: 'Tennis Bayreuth', accent: false },
    { text: 'Mitgliedschaft', accent: false },
    { text: 'Mixed Doubles', accent: false },
    { text: 'Sandplätze', accent: false },
  ];
  const doubled = [...items, ...items];
  return (
    <div className="py-3.5 bg-white overflow-hidden border-y border-[color:var(--svh-border)]">
      <motion.div
        animate={{ x: ['0%', '-50%'] }}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
        className="flex items-center whitespace-nowrap w-max"
      >
        {doubled.map((item, i) => (
          <React.Fragment key={i}>
            <span className="text-[11px] font-semibold tracking-[0.18em] uppercase px-6 text-[color:var(--svh-ink-3)]">
              {item.text}
            </span>
            <span className="w-[2.5px] h-[2.5px] rounded-full bg-red-400/40 flex-shrink-0" />
          </React.Fragment>
        ))}
      </motion.div>
    </div>
  );
};

const courtDetails = [
  {
    image: '/photos/padel-on-court.jpg',
    images: ['/photos/padel-on-court.jpg', '/photos/padel-behind-playing.jpg', '/photos/padel-door.jpg'],
    title: 'Padel Courts',
    subtitle: 'Der einzige Padel-Court in Bayreuth — moderne Anlage, professionelle Ausstattung.',
    tag: 'Padel · Bayreuth',
    number: '01',
    longDesc: 'Erlebe Padel auf höchstem Niveau — der erste und einzige Padel-Court in Bayreuth. Unsere Anlage wurde mit modernster Ausstattung ausgestattet und bietet dir ein einzigartiges Spielerlebnis. Ob Anfänger oder Fortgeschrittener, ob Einzelspiel oder Turnier — hier findest du optimale Bedingungen.',
    details: [
      { icon: 'users', label: 'Spieler', value: '2 vs. 2' },
      { icon: 'zap', label: 'Flutlicht', value: 'Ja' },
      { icon: 'zap', label: 'Ausstattung', value: 'Premium' },
      { icon: 'clock', label: 'Buchung', value: 'Online möglich' },
    ],
  },
  {
    image: '/photos/tennis-court.jpg',
    images: ['/photos/tennis-court.jpg', '/photos/tennis-ball.jpg'],
    title: 'Tennisplätze',
    subtitle: '3 gepflegte Sandplätze für Training und Wettkampf — auch für Turniere geeignet.',
    tag: 'Tennis · Sandplatz',
    number: '02',
    longDesc: 'Drei professionell gepflegte Sandplätze bieten optimale Spielbedingungen für Training und Wettkampf. Die Plätze werden regelmäßig gewartet und eignen sich sowohl für entspannte Trainingseinheiten als auch für offizielle Turniere und Vereinswettbewerbe.',
    details: [
      { icon: 'users', label: 'Plätze', value: '3 Sandplätze' },
      { icon: 'zap', label: 'Turnier', value: 'Geeignet' },
      { icon: 'mappin', label: 'Belag', value: 'Sand' },
      { icon: 'clock', label: 'Verfügbar', value: 'Täglich' },
    ],
  },
  {
    image: '/photos/padel-playing-side.jpg',
    images: ['/photos/padel-playing-side.jpg', '/photos/padel-on-net.jpg'],
    title: 'Flutlicht 23:00',
    subtitle: 'Abendspiele unter professionellem LED-Flutlicht — auch unter der Woche.',
    tag: 'Flutlicht · Abend',
    number: '03',
    longDesc: 'Dank moderner LED-Flutlichtanlage kannst du bis 23:00 Uhr spielen — auch unter der Woche. Das professionelle Licht sorgt für optimale Sichtverhältnisse und ermöglicht dir, deinen Sport auch nach einem langen Arbeitstag zu genießen.',
    details: [
      { icon: 'clock', label: 'Betrieb bis', value: '23:00 Uhr' },
      { icon: 'zap', label: 'Technologie', value: 'LED' },
      { icon: 'users', label: 'Wochentage', value: 'Mo–So' },
      { icon: 'mappin', label: 'Courts', value: 'Alle Plätze' },
    ],
  },
];

type CourtDetailType = typeof courtDetails[0];

const CourtModal: React.FC<{ court: CourtDetailType; onClose: () => void; onBook: () => void }> = ({ court, onClose, onBook }) => {
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setActiveImg(i => (i + 1) % court.images.length);
      if (e.key === 'ArrowLeft') setActiveImg(i => (i - 1 + court.images.length) % court.images.length);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, court.images.length]);

  const iconMap: Record<string, React.ReactNode> = {
    users: <Users className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />,
    mappin: <MapPin className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />,
    zap: <Zap className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />,
    clock: <Clock className="w-3.5 h-3.5 text-red-600 flex-shrink-0" />,
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-8">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="absolute inset-0 bg-black/75 backdrop-blur-lg"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.93, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.93, y: 24 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-2xl bg-white rounded-[24px] shadow-[0_40px_100px_rgba(0,0,0,0.45)] overflow-hidden flex flex-col"
        style={{ maxHeight: 'calc(100vh - 4rem)' }}
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>

        <div className="relative flex-shrink-0 h-[42vh] overflow-hidden bg-black">
          <AnimatePresence mode="wait">
            <motion.img
              key={activeImg}
              src={court.images[activeImg]}
              alt={court.title}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-black/20" />

          {court.images.length > 1 && (
            <>
              <button
                onClick={() => setActiveImg(i => (i - 1 + court.images.length) % court.images.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
              >
                <ChevronDown className="w-3.5 h-3.5 text-white rotate-90" />
              </button>
              <button
                onClick={() => setActiveImg(i => (i + 1) % court.images.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-black/60 transition-colors"
              >
                <ChevronDown className="w-3.5 h-3.5 text-white -rotate-90" />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {court.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`transition-all duration-300 rounded-full ${i === activeImg ? 'w-5 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/65'}`}
                  />
                ))}
              </div>
            </>
          )}

          <div className="absolute bottom-5 left-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[9px] font-bold tracking-[0.18em] uppercase bg-white/15 backdrop-blur-sm text-white px-2.5 py-1 rounded-full border border-white/15">
                {court.tag}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-white leading-tight max-w-xs drop-shadow-lg">{court.title}</h2>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-5 sm:p-7">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {court.details.map((d, i) => (
              <div key={i} className="bg-[color:var(--svh-surface-sub)] border border-[color:var(--svh-border)] rounded-[10px] p-4">
                <div className="mb-2">{iconMap[d.icon]}</div>
                <p className="svh-label mb-1.5">{d.label}</p>
                <p className="text-[13px] font-semibold text-[color:var(--svh-ink)]">{d.value}</p>
              </div>
            ))}
          </div>

          <p className="svh-body mb-6">{court.longDesc}</p>

          <div className="flex items-start gap-2.5 p-4 bg-[color:var(--svh-surface-sub)] border border-[color:var(--svh-border)] rounded-[10px] mb-5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5" />
            <p className="text-[13px] text-[color:var(--svh-ink-3)]">Jetzt verfügbar — online buchbar für Mitglieder und Gäste.</p>
          </div>

          <button
            onClick={() => { onClose(); onBook(); }}
            className="w-full svh-btn svh-btn--primary svh-focus group"
          >
            Jetzt buchen
            <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

const CourtsSection: React.FC<{ onNavigate: (p: Page) => void }> = ({ onNavigate }) => {
  const { ref, inView } = useInView(0.05);
  const [hovered, setHovered] = useState<number | null>(null);
  const [selectedCourt, setSelectedCourt] = useState<CourtDetailType | null>(null);

  const courts = courtDetails;

  return (
    <section ref={ref} className="svh-band svh-band--ink svh-section overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="svh-split lg:items-end mb-14 sm:mb-20">
          <div className="svh-opener">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="svh-opener__tag"
            >
              <span className="svh-label">Unsere Anlage</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.75, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="svh-display-xl"
            >
              Erstklassige<br />Plätze
            </motion.h2>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col gap-7 lg:items-end lg:text-right"
          >
            <p className="svh-lead max-w-sm">
              in Heinersreuth. Der einzige Padel-Court in Bayreuth — Flutlicht bis 23:00 Uhr.
            </p>
            <button
              onClick={() => onNavigate('booking')}
              className="group svh-btn svh-btn--primary svh-focus self-start lg:self-end"
            >
              Platz buchen
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
          {courts.map((court, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 32 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => setSelectedCourt(court)}
              className="group relative overflow-hidden rounded-[16px] cursor-pointer"
              style={{ aspectRatio: '3/4' }}
            >
              <motion.img
                src={court.image}
                alt={court.title}
                className="absolute inset-0 w-full h-full object-cover"
                animate={{ scale: hovered === i ? 1.08 : 1 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-black/5" />

              <motion.div
                className="absolute inset-0 bg-red-600/15"
                animate={{ opacity: hovered === i ? 1 : 0 }}
                transition={{ duration: 0.5 }}
              />

              <div className="absolute inset-0 p-6 flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold tracking-[0.2em] uppercase text-white/50 bg-white/8 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10">
                    {court.tag}
                  </span>
                  <span className="text-[11px] font-bold text-white/20 tabular-nums">{court.number}</span>
                </div>

                <div>
                  <motion.div
                    animate={{ opacity: hovered === i ? 1 : 0, y: hovered === i ? 0 : 8 }}
                    transition={{ duration: 0.4 }}
                    className="mb-3"
                  >
                    <div className="w-6 h-[1.5px] bg-[color:var(--svh-crest)]" />
                  </motion.div>
                  <h3 className="text-[1.4rem] font-bold text-white mb-2.5 leading-tight tracking-tight">{court.title}</h3>
                  <motion.p
                    animate={{ opacity: hovered === i ? 1 : 0.5, y: hovered === i ? 0 : 8 }}
                    transition={{ duration: 0.45 }}
                    className="text-[13px] text-white/60 leading-relaxed"
                  >
                    {court.subtitle}
                  </motion.p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedCourt && (
          <CourtModal
            key={selectedCourt.title}
            court={selectedCourt}
            onClose={() => setSelectedCourt(null)}
            onBook={() => onNavigate('booking')}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

const EditorialSection: React.FC<{ onNavigate: (p: Page) => void }> = ({ onNavigate }) => {
  const { ref, inView } = useInView(0.08);

  return (
    <section ref={ref} className="svh-section bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-center">
          <motion.div
            initial={{ opacity: 0, x: -36 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative aspect-[3/4] rounded-[24px] overflow-hidden">
              <img
                src="/photos/padel-on-net.jpg"
                alt="Padel Net"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" />

              <div className="absolute bottom-5 left-5 right-5">
                <div className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-600/90 flex items-center justify-center shadow-lg shadow-red-900/40">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 1.5C4.515 1.5 2.5 3.515 2.5 6c0 3.5 4.5 7 4.5 7s4.5-3.5 4.5-7c0-2.485-2.015-4.5-4.5-4.5zm0 6.125a1.625 1.625 0 1 1 0-3.25 1.625 1.625 0 0 1 0 3.25z" fill="white"/>
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white/50 text-[10px] font-semibold tracking-[0.18em] uppercase leading-none mb-1">Bayreuth</p>
                      <p className="text-white text-[13px] font-bold leading-none tracking-tight truncate">Erster Padel-Court</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onNavigate('booking')}
                    className="flex-shrink-0 flex items-center gap-1.5 bg-white/8 border border-white/10 hover:bg-white/15 hover:border-white/20 rounded-xl px-3 py-1.5 transition-all duration-200 cursor-pointer"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.9)]" />
                    <span className="text-white/80 text-[10px] font-semibold tracking-[0.12em] uppercase whitespace-nowrap">Jetzt buchbar</span>
                  </button>
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.88 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -top-5 -right-5 sm:-right-8 w-32 sm:w-40 aspect-square rounded-[18px] overflow-hidden border-[3px] border-white shadow-[0_20px_60px_rgba(0,0,0,0.18)]"
            >
              <img
                src="/photos/Image.jpeg"
                alt="SV Club"
                className="w-full h-full object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl px-5 py-4 shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-gray-100"
            >
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-gray-400 mb-1.5">Spielbetrieb</p>
              <p className="text-gray-900 font-bold text-base">08:00 – 23:00</p>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 36 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-7">
              <div className="w-8 h-[1.5px] bg-red-500" />
              <span className="svh-eyebrow">Warum SV Heinersreuth</span>
            </div>

            <h2 className="svh-h2 mb-7">
              Mehr als nur<br />
              <span className="italic font-light text-gray-400">ein Platz.</span>
            </h2>

            <p className="text-gray-400 text-[15px] leading-[1.8] mb-11 max-w-md">
              Erleben Sie Padel und Tennis auf professionellem Niveau — in einer Gemeinschaft, die Ihnen am Herzen liegt. Mit Online-Buchung, Flutlicht und unvergesslichen Turnieren.
            </p>

            <div className="space-y-6 mb-12">
              {[
                { n: '01', title: 'Online buchen', desc: 'In Sekunden zum Platz — sofortige Bestätigung per E-Mail.' },
                { n: '02', title: 'Exklusive Mitgliedschaft', desc: 'Vergünstigte Preise, bevorzugte Buchungszeiten und Vereinsevents.' },
                { n: '03', title: 'Flutlicht bis 23:00', desc: 'Abendmatches unter professionellem LED-Licht — auch im Sommer.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.35 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-6 items-start group"
                >
                  <span className="text-[10px] font-bold text-red-400/70 tracking-widest pt-1 w-5 flex-shrink-0">{item.n}</span>
                  <div className="border-t border-gray-100 pt-4 flex-1">
                    <h4 className="text-[14px] font-bold text-gray-900 mb-1.5 tracking-tight">{item.title}</h4>
                    <p className="text-[13px] text-gray-400 leading-relaxed">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => onNavigate('membership')}
                className="group svh-btn svh-btn--primary svh-focus"
              >
                Mitglied werden
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <button
                onClick={() => onNavigate('events')}
                className="svh-btn svh-btn--secondary svh-focus"
              >
                Events ansehen
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const GalleryStrip: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: containerRef, offset: ['start end', 'end start'] });
  const x1 = useTransform(scrollYProgress, [0, 1], ['2%', '-10%']);
  const x2 = useTransform(scrollYProgress, [0, 1], ['-4%', '10%']);

  const row1 = [
    '/photos/padel-on-court.jpg',
    '/photos/padel-playing-side.jpg',
    '/photos/Image.jpeg',
    '/photos/tennis-court.jpg',
    '/photos/padel-on-net.jpg',
  ];
  const row2 = [
    '/photos/padel-door.jpg',
    '/photos/padel-behind-playing.jpg',
    '/photos/tennis-ball.jpg',
    '/photos/padel-on-court.jpg',
    '/photos/Image.jpeg',
  ];

  return (
    <div ref={containerRef} className="py-14 sm:py-20 bg-white overflow-hidden border-y border-gray-100">
      <div className="mb-4">
        <motion.div style={{ x: x1 }} className="flex gap-3 sm:gap-4 w-max">
          {[...row1, ...row1].map((src, i) => (
            <div key={i} className="w-56 h-40 sm:w-80 sm:h-56 rounded-[16px] overflow-hidden flex-shrink-0">
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </motion.div>
      </div>
      <div>
        <motion.div style={{ x: x2 }} className="flex gap-3 sm:gap-4 w-max ml-[-28px]">
          {[...row2, ...row2].map((src, i) => (
            <div key={i} className="w-56 h-40 sm:w-80 sm:h-56 rounded-[16px] overflow-hidden flex-shrink-0">
              <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

const CtaSection: React.FC<{ onNavigate: (p: Page) => void }> = ({ onNavigate }) => {
  const { ref, inView } = useInView(0.15);

  return (
    <section ref={ref} className="relative svh-band svh-band--ink svh-section overflow-hidden">
      <div className="relative z-10 max-w-5xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 36 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="svh-opener__tag mb-7">
            <span className="svh-label">Bereit?</span>
          </div>

          <h2 className="svh-display-xl mb-8">
            Einfach online buchen.
          </h2>

          <div className="svh-split lg:items-end">
            <p className="svh-lead max-w-md">
              Datum wählen, Uhrzeit wählen, fertig. Sofortbestätigung per E-Mail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 lg:justify-end">
            <motion.button
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onNavigate('booking')}
              className="svh-btn svh-btn--primary svh-focus"
            >
              Platz buchen
              <ArrowRight size={16} />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onNavigate('contact')}
              className="svh-btn svh-btn--secondary svh-focus"
            >
              Kontakt
            </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const HomePage: React.FC<HomePageProps> = ({ onNavigate }) => {
  return (
    <div className="-mt-20 sm:-mt-24">
      <HeroSection onNavigate={onNavigate} />
      <MarqueeBar />
      <CourtsSection onNavigate={onNavigate} />
      <EditorialSection onNavigate={onNavigate} />
      <GalleryStrip />
      <CtaSection onNavigate={onNavigate} />
    </div>
  );
};

export default HomePage;
